import { Alert, Dialog, TextareaAutosize } from "@mui/material";
import { isEqual } from "lodash";
import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AnomalyTrackingNotificationMetaDataDTO } from "src/models/tna/tna-anomalies/anomaly-tracking-notification-meta-data-dto";
import AddEditButton from "src/reusable-components/AddEditButton/AddEditButton";
import { State, Props } from "./interfaces";
import { DialogContent } from "./styles";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../DialogActions/DialogActions";
import DialogTitle from "../../../DialogTitle/DialogTitle";

const AddEditAppendedComment = ({
  item,
  handleNotifyParent,
  onLocalSave,
  children,
  viewMode = false,
  buttonText,
  effect,
  disabled,
  customButtonStyle,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.edit_comment_modal";

  const DEFAULT_ITEM = useMemo(
    () =>
      ({
        ...item,
        lastComment: item?.lastComment ?? "",
      } as AnomalyTrackingNotificationMetaDataDTO),
    [item]
  );

  const [state, setState] = useState<State>({
    openDialog: false,
    loading: false,
    item: item ?? DEFAULT_ITEM,
    error: "",
    CCDOpen: false,
  });

  const handleChangeField = useCallback((value: string) => {
    setState((state) => ({
      ...state,
      item: { ...state.item, lastComment: value },
    }));
  }, []);

  const hasChanges = useCallback((): boolean => {
    return !isEqual({ comment: state.item.lastComment }, { comment: item?.lastComment ?? "" });
  }, [state.item, item]);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      if (onLocalSave) {
        onLocalSave(state.item);
        setState((state) => ({ ...state, openDialog: false, loading: false }));
        return;
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string, loading: false }));
    }
  }, [onLocalSave, state.item]);

  const areFieldsValidated = useCallback((): boolean => {
    return !!state.item.lastComment && hasChanges();
  }, [state.item, hasChanges]);

  return (
    <>
      <AddEditButton
        title={state.item.lastComment ? t(`${tRP}.edit_title`) : t(`${tRP}.add_title`)}
        effect={effect}
        disabled={disabled}
        onClick={() => {
          setState((state) => ({ ...state, openDialog: true }));
        }}
        buttonText={buttonText}
        customStyle={customButtonStyle}
      >
        {children}
      </AddEditButton>
      <Dialog
        open={state.openDialog}
        onClose={() => {
          if (hasChanges()) {
            setState((state) => ({ ...state, CCDOpen: true }));
          } else {
            setState((state) => ({ ...state, openDialog: false }));
          }
        }}
        fullWidth
        // sx={{
        //   ".MuiDialogContent-root": {
        //     height: "120px !important",
        //     minHeight: "120px !important",
        //   },
        // }}
      >
        <DialogTitle
          title={t(`${tRP}.edit_title`)}
          handleClose={() => {
            if (hasChanges()) {
              setState((state) => ({ ...state, CCDOpen: true }));
            } else {
              setState((state) => ({ ...state, openDialog: false }));
            }
          }}
        />
        <DialogContent>
          <TextareaAutosize
            minRows={4}
            maxRows={8}
            placeholder={t(`${tRP}.fields.comment_placeholder`)}
            value={state.item.lastComment}
            onChange={(e) => handleChangeField(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontFamily: "inherit",
              fontSize: "14px",
              resize: "none",
            }}
            disabled={viewMode}
          />
          <Alert
            severity="info"
            style={{ marginTop: "3px" }}
          >
            {t(`${tRP}.alerts.comment_notification`)}{" "}
          </Alert>
        </DialogContent>
        <DialogActions
          handleCancel={() => {
            if (hasChanges()) {
              setState((state) => ({ ...state, CCDOpen: true }));
            } else {
              setState((state) => ({ ...state, openDialog: false }));
            }
          }}
          handleSave={handleSave}
          loading={state.loading}
          disabledSaveButton={viewMode || !areFieldsValidated()}
          error={state.error}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            openDialog: false,
            item: DEFAULT_ITEM,
            CCDOpen: false,
            error: "",
          }))
        }
      />
    </>
  );
};

export default React.memo(AddEditAppendedComment);
